import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import type { AuthenticatedUser } from '@busbook/shared';
import { api } from './api.js';

/**
 * Customer accounts are per tenant (docs/01): the same person may hold separate
 * accounts with two operators, and a session never crosses between them.
 * An account is optional — guest checkout is a first-class path.
 */

interface SessionValue {
  user: AuthenticatedUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  register: (input: { email: string; password: string; name: string }) => Promise<void>;
  signOut: () => void;
}

const Ctx = createContext<SessionValue | null>(null);

export function useCustomer(): SessionValue {
  const value = useContext(Ctx);
  if (!value) throw new Error('useCustomer must be used inside <CustomerSessionProvider>');
  return value;
}

interface AuthPayload {
  user: AuthenticatedUser;
  tokens: { accessToken: string; refreshToken: string };
}

export function CustomerSessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      if (!api.isAuthenticated()) {
        setLoading(false);
        return;
      }
      try {
        setUser(await api.get<AuthenticatedUser>('/public/auth/me'));
      } catch {
        setUser(null);
      } finally {
        setLoading(false);
      }
    })();

    const onLost = () => setUser(null);
    window.addEventListener('busbook:auth-lost', onLost);
    return () => window.removeEventListener('busbook:auth-lost', onLost);
  }, []);

  const complete = useCallback((result: AuthPayload) => {
    api.setTokens(result.tokens);
    setUser(result.user);
  }, []);

  const signIn = useCallback(
    async (email: string, password: string) => {
      complete(
        await api.request<AuthPayload>('/public/auth/login', {
          method: 'POST',
          auth: false,
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ email, password }),
        }),
      );
    },
    [complete],
  );

  const register = useCallback(
    async (input: { email: string; password: string; name: string }) => {
      complete(
        await api.request<AuthPayload>('/public/auth/register', {
          method: 'POST',
          auth: false,
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify(input),
        }),
      );
    },
    [complete],
  );

  const signOut = useCallback(() => {
    api.setTokens(null);
    setUser(null);
  }, []);

  return (
    <Ctx.Provider value={{ user, loading, signIn, register, signOut }}>{children}</Ctx.Provider>
  );
}
